import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
    new: { label: 'New', color: '#ef4444', bg: '#fef2f2', icon: 'fa-solid fa-bell' },
    contacted: { label: 'Contacted', color: '#f59e0b', bg: '#fffbeb', icon: 'fa-solid fa-phone' },
    resolved: { label: 'Resolved', color: '#10b981', bg: '#f0fdf4', icon: 'fa-solid fa-circle-check' },
};

const TYPE_CONFIG = {
    flight: { label: 'Flight', color: '#0ea5e9', bg: '#eff6ff', icon: 'fa-solid fa-plane' },
    visa: { label: 'Visa', color: '#8b5cf6', bg: '#f5f3ff', icon: 'fa-solid fa-passport' },
    tour: { label: 'Tour', color: '#f97316', bg: '#fff7ed', icon: 'fa-solid fa-map-location-dot' },
    cruise: { label: 'Cruise', color: '#0284c7', bg: '#e0f2fe', icon: 'fa-solid fa-ship' },
    shortbreak: { label: 'Short Break', color: '#fbbf24', bg: '#fef3c7', icon: 'fa-solid fa-calendar-days' },
    contact: { label: 'Contact', color: '#059669', bg: '#ecfdf5', icon: 'fa-solid fa-address-card' },
};

const typeConf = (t) => TYPE_CONFIG[t] || { label: t, color: '#64748b', bg: '#f8fafc', icon: 'fa-solid fa-envelope' };
const statusConf = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.new;

const fmtDate = (dt) =>
    dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return fmtDate(dt);
};

/* Parse the notes field that FlightInquiry packs as:
   "Trip: round | Return: 2026-03-27 | Class: business | Notes: ..." */
const parseFlightNotes = (notesStr = '') => {
    const parts = {};
    notesStr.split('|').forEach(seg => {
        const [k, ...rest] = seg.split(':');
        if (k) parts[k.trim().toLowerCase()] = rest.join(':').trim();
    });
    return {
        tripType: parts['trip'] || '',
        returnDate: parts['return'] || '',
        cabinClass: parts['class'] || '',
        userNotes: parts['notes'] || '',
    };
};

const CABIN_ICONS = {
    economy: { icon: 'fa-solid fa-person-seat', label: 'Economy' },
    premium: { icon: 'fa-solid fa-person-seat', label: 'Premium Eco' },
    business: { icon: 'fa-solid fa-briefcase', label: 'Business' },
    first: { icon: 'fa-solid fa-crown', label: 'First Class' },
};

/* ── FLIGHT CARD ─────────────────────────────────────────────────────────── */
const FlightCard = ({ inq, onStatusChange }) => {
    const { tripType, returnDate, cabinClass, userNotes } = parseFlightNotes(inq.notes);
    const sc = statusConf(inq.status);
    const cabin = CABIN_ICONS[cabinClass] || { icon: 'fa-solid fa-chair', label: cabinClass || 'Economy' };

    return (
        <div className="inq-card flight-card">
            {/* Card Header */}
            <div className="inq-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#eff6ff', color: '#0ea5e9', fontSize: 15, flexShrink: 0,
                    }}>
                        <i className="fa-solid fa-plane-departure" />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>{inq.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                            <i className="fa-solid fa-clock" style={{ marginRight: 4 }} />{fmtTime(inq.created_at)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Status badge */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: sc.bg, color: sc.color, border: `1px solid ${sc.color}33`
                    }}>
                        <i className={sc.icon} /> {sc.label}
                    </span>
                    {/* Status changer */}
                    <select
                        className="admin-status-select"
                        value={inq.status}
                        onChange={e => onStatusChange(inq.id, e.target.value)}
                        style={{ fontSize: 12 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                            <option key={v} value={v}>{c.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Route hero */}
            <div className="flight-route-bar">
                <div className="flight-city">
                    <div className="flight-city-code">{(inq.from_city || '').split('(')[1]?.replace(')', '') || '—'}</div>
                    <div className="flight-city-name">{(inq.from_city || '').split('(')[0].trim() || 'From'}</div>
                </div>
                <div className="flight-route-mid">
                    <div className="flight-route-line">
                        <div className="flight-route-dot" />
                        <div className="flight-route-dash" />
                        <i className="fa-solid fa-plane flight-plane-icon" />
                        <div className="flight-route-dash" />
                        <div className="flight-route-dot" />
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4, textAlign: 'center' }}>
                        {tripType === 'round' ? '⇄ Round Trip' : tripType === 'multi' ? '↔ Multi-City' : '→ One Way'}
                    </div>
                </div>
                <div className="flight-city" style={{ textAlign: 'right' }}>
                    <div className="flight-city-code">{(inq.to_city || '').split('(')[1]?.replace(')', '') || '—'}</div>
                    <div className="flight-city-name">{(inq.to_city || '').split('(')[0].trim() || 'To'}</div>
                </div>
            </div>

            {/* Detail chips */}
            <div className="flight-chips">
                <span className="flight-chip">
                    <i className="fa-solid fa-calendar-day" />
                    {inq.travel_date ? fmtDate(inq.travel_date) : '—'}
                    {returnDate && returnDate !== 'N/A' ? ` → ${fmtDate(returnDate)}` : ''}
                </span>
                <span className="flight-chip">
                    <i className="fa-solid fa-users" />
                    {inq.passengers || 1} passenger{inq.passengers > 1 ? 's' : ''}
                </span>
                <span className="flight-chip">
                    <i className={cabin.icon} />
                    {cabin.label}
                </span>
            </div>

            {/* Contacts + Notes */}
            <div className="inq-contact-row">
                <a href={`tel:${inq.phone}`} className="inq-contact-chip">
                    <i className="fa-solid fa-phone" /> {inq.phone}
                </a>
                <a href={`mailto:${inq.email}`} className="inq-contact-chip">
                    <i className="fa-solid fa-envelope" /> {inq.email}
                </a>
            </div>

            {userNotes && (
                <div className="inq-notes">
                    <i className="fa-solid fa-note-sticky" /> {userNotes}
                </div>
            )}
        </div>
    );
};

/* ── VISA / TOUR / GENERIC CARD ──────────────────────────────────────────── */
const GenericCard = ({ inq, onStatusChange }) => {
    const tc = typeConf(inq.type);
    const sc = statusConf(inq.status);

    return (
        <div className="inq-card">
            <div className="inq-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: tc.bg, color: tc.color, fontSize: 15, flexShrink: 0,
                    }}>
                        <i className={tc.icon} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>{inq.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                            <span style={{ background: tc.bg, color: tc.color, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, marginRight: 6 }}>
                                <i className={tc.icon} style={{ marginRight: 4 }} />{tc.label}
                            </span>
                            <i className="fa-solid fa-clock" style={{ marginRight: 4 }} />{fmtTime(inq.created_at)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: sc.bg, color: sc.color, border: `1px solid ${sc.color}33`
                    }}>
                        <i className={sc.icon} /> {sc.label}
                    </span>
                    <select className="admin-status-select" value={inq.status}
                        onChange={e => onStatusChange(inq.id, e.target.value)} style={{ fontSize: 12 }}>
                        {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                            <option key={v} value={v}>{c.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="inq-detail-grid">
                {inq.country && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-globe" />
                        <div><span className="inq-detail-label">Country / Destination</span><span className="inq-detail-val">{inq.country}</span></div>
                    </div>
                )}
                {inq.travel_date && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-calendar" />
                        <div><span className="inq-detail-label">Travel Date</span><span className="inq-detail-val">{fmtDate(inq.travel_date)}</span></div>
                    </div>
                )}
            </div>

            <div className="inq-contact-row">
                <a href={`tel:${inq.phone}`} className="inq-contact-chip">
                    <i className="fa-solid fa-phone" /> {inq.phone}
                </a>
                <a href={`mailto:${inq.email}`} className="inq-contact-chip">
                    <i className="fa-solid fa-envelope" /> {inq.email}
                </a>
            </div>

            {inq.notes && (
                <div className="inq-notes">
                    <i className="fa-solid fa-note-sticky" /> {inq.notes}
                </div>
            )}
        </div>
    );
};

/* ── CONTACT CARD ────────────────────────────────────────────────────────── */
const parseContactNotes = (notesStr = '') => {
    const parts = {};
    notesStr.split('|').forEach(seg => {
        const [k, ...rest] = seg.split(':');
        if (k) parts[k.trim().toLowerCase()] = rest.join(':').trim();
    });
    return {
        tripType: parts['trip'] || '',
        budget: parts['budget'] || '',
        userNotes: parts['notes'] || '',
    };
};

const ContactCard = ({ inq, onStatusChange }) => {
    const { tripType, budget, userNotes } = parseContactNotes(inq.notes);
    const tc = TYPE_CONFIG.contact;
    const sc = statusConf(inq.status);

    return (
        <div className="inq-card">
            <div className="inq-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: tc.bg, color: tc.color, fontSize: 15, flexShrink: 0,
                    }}>
                        <i className={tc.icon} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>{inq.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                            <span style={{ background: tc.bg, color: tc.color, padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, marginRight: 6 }}>
                                <i className={tc.icon} style={{ marginRight: 4 }} />{tc.label}
                            </span>
                            <i className="fa-solid fa-clock" style={{ marginRight: 4 }} />{fmtTime(inq.created_at)}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: sc.bg, color: sc.color, border: `1px solid ${sc.color}33`
                    }}>
                        <i className={sc.icon} /> {sc.label}
                    </span>
                    <select className="admin-status-select" value={inq.status}
                        onChange={e => onStatusChange(inq.id, e.target.value)} style={{ fontSize: 12 }}>
                        {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                            <option key={v} value={v}>{c.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="inq-detail-grid">
                {inq.country && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-globe" />
                        <div><span className="inq-detail-label">Destination</span><span className="inq-detail-val">{inq.country}</span></div>
                    </div>
                )}
                {tripType && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-suitcase-rolling" />
                        <div><span className="inq-detail-label">Trip Type</span><span className="inq-detail-val">{tripType}</span></div>
                    </div>
                )}
                {inq.passengers && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-users" />
                        <div><span className="inq-detail-label">Travellers</span><span className="inq-detail-val">{inq.passengers}</span></div>
                    </div>
                )}
                {inq.travel_date && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-calendar" />
                        <div><span className="inq-detail-label">Travel Date</span><span className="inq-detail-val">{fmtDate(inq.travel_date)}</span></div>
                    </div>
                )}
                {budget && (
                    <div className="inq-detail-item">
                        <i className="fa-solid fa-indian-rupee-sign" />
                        <div><span className="inq-detail-label">Budget</span><span className="inq-detail-val">{budget}</span></div>
                    </div>
                )}
            </div>

            <div className="inq-contact-row">
                <a href={`tel:${inq.phone}`} className="inq-contact-chip">
                    <i className="fa-solid fa-phone" /> {inq.phone}
                </a>
                {inq.email && (
                    <a href={`mailto:${inq.email}`} className="inq-contact-chip">
                        <i className="fa-solid fa-envelope" /> {inq.email}
                    </a>
                )}
            </div>

            {userNotes && (
                <div className="inq-notes">
                    <i className="fa-solid fa-note-sticky" /> {userNotes}
                </div>
            )}
        </div>
    );
};

/* ── MAIN TAB ─────────────────────────────────────────────────────────────── */
const FILTERS = [
    { key: 'all', label: 'All', icon: 'fa-solid fa-inbox' },
    { key: 'new', label: 'New', icon: 'fa-solid fa-bell' },
    { key: 'flight', label: 'Flights', icon: 'fa-solid fa-plane' },
    { key: 'cruise', label: 'Cruises', icon: 'fa-solid fa-ship' },
    { key: 'shortbreak', label: 'Short Breaks', icon: 'fa-solid fa-calendar-days' },
    { key: 'visa', label: 'Visa', icon: 'fa-solid fa-passport' },
    { key: 'contact', label: 'Contact', icon: 'fa-solid fa-address-card' },
    { key: 'contacted', label: 'Contacted', icon: 'fa-solid fa-phone' },
    { key: 'resolved', label: 'Resolved', icon: 'fa-solid fa-circle-check' },
];

const InquiriesTab = ({ showToast }) => {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

    useEffect(() => { fetchInquiries(); }, []);

    const fetchInquiries = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('inquiries')
            .select('*')
            .order('created_at', { ascending: false });
        if (!error) setInquiries(data || []);
        setLoading(false);
    };

    const updateStatus = async (id, status) => {
        const { error } = await supabase.from('inquiries').update({ status }).eq('id', id);
        if (!error) {
            setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
            showToast('Status updated', 'success');
        } else {
            showToast('Error updating status', 'error');
        }
    };

    const counts = {
        all: inquiries.length,
        new: inquiries.filter(i => i.status === 'new').length,
        flight: inquiries.filter(i => i.type === 'flight').length,
        cruise: inquiries.filter(i => i.type === 'cruise').length,
        shortbreak: inquiries.filter(i => i.type === 'shortbreak').length,
        visa: inquiries.filter(i => i.type === 'visa').length,
        contact: inquiries.filter(i => i.type === 'contact').length,
        contacted: inquiries.filter(i => i.status === 'contacted').length,
        resolved: inquiries.filter(i => i.status === 'resolved').length,
    };

    const filtered = inquiries.filter(i => {
        const matchFilter =
            filter === 'all' ? true :
                filter === 'new' ? i.status === 'new' :
                    filter === 'contacted' ? i.status === 'contacted' :
                        filter === 'resolved' ? i.status === 'resolved' :
                            i.type === filter;
        const matchSearch = !search || [i.name, i.phone, i.email, i.country, i.from_city, i.to_city]
            .some(v => v && v.toLowerCase().includes(search.toLowerCase()));
        return matchFilter && matchSearch;
    });

    return (
        <div className="admin-tab-content">

            {/* ── Stats ── */}
            <div className="admin-stats-row">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon gold"><i className="fa-solid fa-inbox" /></div>
                    <div><div className="admin-stat-val">{counts.all}</div><div className="admin-stat-lbl">Total</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon red"><i className="fa-solid fa-bell" /></div>
                    <div><div className="admin-stat-val">{counts.new}</div><div className="admin-stat-lbl">New / Unread</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon blue"><i className="fa-solid fa-plane" /></div>
                    <div><div className="admin-stat-val">{counts.flight}</div><div className="admin-stat-lbl">Flight</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon purple"><i className="fa-solid fa-passport" /></div>
                    <div><div className="admin-stat-val">{counts.visa}</div><div className="admin-stat-lbl">Visa</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon" style={{ background: '#ecfdf5', color: '#059669' }}><i className="fa-solid fa-address-card" /></div>
                    <div><div className="admin-stat-val">{counts.contact}</div><div className="admin-stat-lbl">Contact</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon green"><i className="fa-solid fa-circle-check" /></div>
                    <div><div className="admin-stat-val">{counts.resolved}</div><div className="admin-stat-lbl">Resolved</div></div>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                {/* Filter tabs */}
                <div className="admin-filter-bar" style={{ flex: 1, flexWrap: 'wrap', marginBottom: 0 }}>
                    {FILTERS.map(f => (
                        <button key={f.key} className={`admin-filter-btn ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
                            <i className={f.icon} style={{ marginRight: 5 }} />
                            {f.label}
                            {f.key !== 'all' && counts[f.key] > 0 && (
                                <span style={{ marginLeft: 4, fontWeight: 700 }}>({counts[f.key]})</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="admin-search-wrap" style={{ flexShrink: 0 }}>
                    <i className="fa-solid fa-search" />
                    <input className="admin-search" placeholder="Search name, phone, route…"
                        value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
                </div>

                {/* View toggle */}
                <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                    <button onClick={() => setViewMode('cards')}
                        style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 13, background: viewMode === 'cards' ? 'var(--gold)' : 'var(--bg-card)', color: viewMode === 'cards' ? '#fff' : 'var(--text-2)' }}>
                        <i className="fa-solid fa-grip" />
                    </button>
                    <button onClick={() => setViewMode('table')}
                        style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 13, background: viewMode === 'table' ? 'var(--gold)' : 'var(--bg-card)', color: viewMode === 'table' ? '#fff' : 'var(--text-2)' }}>
                        <i className="fa-solid fa-list" />
                    </button>
                </div>
            </div>

            {/* ── Content ── */}
            {loading ? (
                <div className="admin-loading"><div className="admin-spinner" /><p>Loading inquiries…</p></div>
            ) : filtered.length === 0 ? (
                <div className="admin-empty"><i className="fa-solid fa-inbox" /><p>No inquiries found.</p></div>
            ) : viewMode === 'cards' ? (

                /* ── CARD VIEW ── */
                <div className="inq-cards-grid">
                    {filtered.map(inq =>
                        inq.type === 'flight'
                            ? <FlightCard key={inq.id} inq={inq} onStatusChange={updateStatus} />
                            : inq.type === 'contact'
                                ? <ContactCard key={inq.id} inq={inq} onStatusChange={updateStatus} />
                                : <GenericCard key={inq.id} inq={inq} onStatusChange={updateStatus} />
                    )}
                </div>

            ) : (

                /* ── TABLE VIEW ── */
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Name</th>
                                <th>Phone</th>
                                <th>Route / Country</th>
                                <th>Pax</th>
                                <th>Travel Date</th>
                                <th>Received</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(inq => {
                                const tc = typeConf(inq.type);
                                const sc = statusConf(inq.status);
                                return (
                                    <tr key={inq.id}>
                                        <td>
                                            <span style={{ background: tc.bg, color: tc.color, padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                <i className={tc.icon} style={{ marginRight: 4 }} />{tc.label}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{inq.name}</td>
                                        <td>
                                            <a href={`tel:${inq.phone}`} style={{ color: 'var(--text-1)', textDecoration: 'none' }}>{inq.phone}</a>
                                        </td>
                                        <td>
                                            {inq.type === 'flight'
                                                ? <span style={{ fontWeight: 600 }}>{inq.from_city?.split('(')[0].trim() || '—'} <i className="fa-solid fa-arrow-right" style={{ fontSize: 10 }} /> {inq.to_city?.split('(')[0].trim() || '—'}</span>
                                                : inq.country || '—'
                                            }
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{inq.passengers || '—'}</td>
                                        <td style={{ color: 'var(--text-2)' }}>{fmtDate(inq.travel_date)}</td>
                                        <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{fmtTime(inq.created_at)}</td>
                                        <td>
                                            <select className="admin-status-select" value={inq.status}
                                                onChange={e => updateStatus(inq.id, e.target.value)} style={{ fontSize: 12 }}>
                                                {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                                                    <option key={v} value={v}>{c.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default InquiriesTab;
